import { useCallback, useEffect, useMemo, useState } from 'react'
import { Home } from './screens/Home'
import { Session } from './screens/Session'
import { Result } from './screens/Result'
import { Done } from './screens/Done'
import { Settings } from './screens/Settings'
import { Sheet } from './components/Sheet'
import { Toast } from './components/Toast'
import {
  bumpStreak,
  bumpWrong,
  createStorage,
  markMastered,
  resetAll as resetAllState,
  resetLevel as resetLevelState,
  setSessionSize as setSessionSizeState,
} from './logic/storage'
import { advance, buildSession, type SessionQueue } from './logic/session'
import rules from './data/rules.json'
import data from './data/exercises.json'
import type {
  Exercise,
  ExercisesFile,
  LevelMeta,
  RuleKey,
  StorageState,
} from './logic/types'

type Screen = 'home' | 'session' | 'result' | 'done'

const dataTyped = data as ExercisesFile
const rulesTyped = rules as Record<RuleKey, { title: string; ru: string }>

const storage = createStorage()

export default function App() {
  // ── State ──────────────────────────────────────────────────────────
  const [screen, setScreen] = useState<Screen>('home')
  const [state, setState] = useState<StorageState>(() => storage.load())
  const [session, setSession] = useState<SessionQueue | null>(null)
  const [activeLevel, setActiveLevel] = useState<LevelMeta | null>(null)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [toast, setToast] = useState<{ show: boolean; message: string; action?: { label: string; onClick: () => void } }>({
    show: false,
    message: '',
  })

  // Persist on every state change (write-through).
  useEffect(() => {
    storage.save(state)
  }, [state])

  // Listen for "new version available" from main.tsx (service worker update).
  // The toast exposes a Reload action that sends SKIP_WAITING to the new SW;
  // main.tsx's controllerchange listener then triggers location.reload().
  useEffect(() => {
    function onSwUpdate() {
      setToast({
        show: true,
        message: 'Доступна новая версия',
        action: {
          label: 'Перезагрузить',
          onClick: () => {
            navigator.serviceWorker?.controller?.postMessage('SKIP_WAITING')
            // controllerchange handler in main.tsx will reload the page
            setTimeout(() => window.location.reload(), 300)
          },
        },
      })
    }
    window.addEventListener('app:sw-update', onSwUpdate)
    return () => window.removeEventListener('app:sw-update', onSwUpdate)
  }, [])

  // ── Indexes ────────────────────────────────────────────────────────
  const levels = dataTyped.levels
  const exercisesById = useMemo(() => {
    const m = new Map<string, Exercise>()
    for (const e of dataTyped.exercises) m.set(e.id, e)
    return m
  }, [])

  const exercisesByLevel = useMemo(() => {
    const m: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 }
    for (const e of dataTyped.exercises) m[e.level] = (m[e.level] ?? 0) + 1
    return m
  }, [])

  const masteredByLevel = useMemo(() => {
    const m: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0 }
    for (const id of Object.keys(state.mastered)) {
      const lv = Number(id[1])
      if (lv >= 1 && lv <= 4) m[lv] = (m[lv] ?? 0) + 1
    }
    return m
  }, [state.mastered])

  // ── Handlers ───────────────────────────────────────────────────────
  const startLevel = useCallback(
    (level: number) => {
      const lv = levels.find((l) => l.level === level)
      if (!lv) return
      const sess = buildSession(dataTyped.exercises, state, level)
      if (!sess) {
        // level already complete → show Done
        setActiveLevel(lv)
        setSession(null)
        setScreen('done')
        return
      }
      sess.queueIndex = 0
      setActiveLevel(lv)
      setSession(sess)
      setScreen('session')
    },
    [levels, state],
  )

  const handleAnswer = useCallback(
    (id: string, correct: boolean) => {
      setState((s) => {
        if (correct) {
          // first correct of the local day → bump streak
          const wasStreakDay = s.stats.streak.lastDay === todayLocal()
          const next = markMastered(s, id)
          return wasStreakDay ? next : bumpStreak(next)
        }
        return bumpWrong(s, id)
      })
    },
    [],
  )

  const handleAdvance = useCallback((result: 'good' | 'bad') => {
    setSession((sess) => {
      if (!sess) return null
      const withResult = advance(sess, result)
      const next = { ...withResult, queueIndex: withResult.queueIndex + 1 }
      if (next.queueIndex >= next.queue.length) {
        setScreen('result')
      }
      return next
    })
  }, [])

  const handleExitSession = useCallback(() => {
    setSession(null)
    setActiveLevel(null)
    setScreen('home')
  }, [])

  const handleAgain = useCallback(() => {
    if (!activeLevel) return
    startLevel(activeLevel.level)
  }, [activeLevel, startLevel])

  const handleHome = useCallback(() => {
    setSession(null)
    setActiveLevel(null)
    setScreen('home')
  }, [])

  const handleResetLevel = useCallback(
    (level: number) => {
      setState((s) => resetLevelState(s, level))
    },
    [],
  )

  const handleResetAll = useCallback(() => {
    setState((s) => resetAllState(s))
  }, [])

  const handleSetSessionSize = useCallback((size: 10 | 25 | 50) => {
    setState((s) => setSessionSizeState(s, size))
  }, [])

  const showToast = useCallback((message: string) => {
    setToast({ show: true, message })
  }, [])

  // ── Render ─────────────────────────────────────────────────────────
  // Determine if we should be on the "done" screen (mastered-all) when starting.
  // The startLevel function handles this — if buildSession returns null we go to done.

  if (screen === 'home' || (!session && screen === 'session')) {
    return (
      <Shell>
        <Home
          levels={levels}
          exercisesByLevel={exercisesByLevel}
          state={state}
          onPickLevel={startLevel}
          onOpenSettings={() => setSettingsOpen(true)}
        />
        <Settings
          open={settingsOpen}
          state={state}
          levels={levels}
          masteredByLevel={masteredByLevel}
          onClose={() => setSettingsOpen(false)}
          onSetSessionSize={handleSetSessionSize}
          onResetLevel={handleResetLevel}
          onResetAll={handleResetAll}
          onToast={showToast}
        />
        <Toast
          message={toast.message}
          show={toast.show}
          onHide={() => setToast((t) => ({ ...t, show: false }))}
          action={toast.action}
        />
      </Shell>
    )
  }

  if (screen === 'session' && session && activeLevel) {
    return (
      <Shell>
        <Session
          level={activeLevel}
          session={session}
          rules={rulesTyped}
          onAnswer={handleAnswer}
          onAdvance={handleAdvance}
          onExit={handleExitSession}
        />
      </Shell>
    )
  }

  if (screen === 'result' && session && activeLevel) {
    return (
      <Shell>
        <Result
          level={activeLevel}
          total={session.queue.length}
          goodCount={session.good.length}
          badCount={session.bad.length}
          wrongIds={session.bad}
          exercisesById={exercisesById}
          rules={rulesTyped}
          onAgain={handleAgain}
          onHome={handleHome}
        />
      </Shell>
    )
  }

  if (screen === 'done' && activeLevel) {
    return (
      <Shell>
        <Done
          level={activeLevel}
          onResetAndStart={() => {
            setState((s) => resetLevelState(s, activeLevel.level))
            // start next tick
            setTimeout(() => startLevel(activeLevel.level), 0)
          }}
          onHome={handleHome}
        />
      </Shell>
    )
  }

  return null
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="stage">
      <div className="deskcap">Hebben of zijn? · мобильный тренажёр</div>
      <div className="phone">{children}</div>
    </div>
  )
}

function todayLocal(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Suppress unused-import lint for Sheet (kept for future variants)
void Sheet
